<script setup>

    import { util, handleBlur } from '@/Helpers'
    import { useClientInvoiceShiftStore, useClientShiftStore } from '@/Stores'
    import { ClipboardDocumentListIcon } from '@heroicons/vue/20/solid'
    import { computed, watch } from 'vue'
    import { useNotificationStore } from '@/Stores'

    const emit = defineEmits(['shifts:copy'])

    const clientShiftStore = useClientShiftStore()
    const clientInvoiceShiftStore = useClientInvoiceShiftStore()
    const notificationStore = useNotificationStore()


    const props = defineProps({
        selected_shift_ids: {
            type: Array,
            required: true
        },
        start_date: {
            type: String,
            required: true
        },
        client: {
            type: [Object, null],
            required: true
        },
        planned_shifts: {
            type: Array,
            required: false,
            default: () => []
        },
    })

    let clientShifts
    let contextHint
    let canCopy

    watch([() => props.client, () => props.start_date], ([client, start_date]) => {
        if (start_date >= util.date.lastSunday()) {
            clientShifts = clientShiftStore.computedClientShift(client?.id)
        } else {
            clientShifts = clientInvoiceShiftStore.computedClientShift(client?.id)
        }

        contextHint = computed(() => {
            if (props.start_date < util.date.lastSunday()) {
                return getContextHintForOldWeek();
            }

            if (props.planned_shifts.length == 0 && props.selected_shift_ids.length == 0) {
                if (clientShifts.value.length == 0) {
                    return getNoDataContextHint();
                }

                let tipHtml = getUnassignedCareContextHint()
                let scheduleHintHtml = getScheduledHint()

                if (tipHtml !== '' && scheduleHintHtml !== '') {
                    tipHtml += '<br>'
                }
                tipHtml += scheduleHintHtml

                return tipHtml;
            }

            if (props.selected_shift_ids.length) {
                return getEditShiftContextHint();
            }

            if (props.planned_shifts.length) {
                return getAddShiftContextHint();
            }

            return '';
        })

        canCopy = computed(() => {
            if (clientShifts && clientShifts.value.length == 0) { return false; }
            return true;
        })
    }, {immediate: true})

    function getContextHintForOldWeek() {
        let message = `<span class="font-semibold text-lifeworx-blue-900 ">Historical Invoicing Data: </span>`
        let duration = 0
        let caregivers = new Set()

        const nextSunday = util.date.nextSunday(props.start_date)

        clientShifts.value.forEach(shift => {
            if (shift.start_date >= props.start_date && shift.start_date < nextSunday) {
                duration += shift.duration
                if (shift.assigned_caregiver.id) {
                    caregivers.add(shift.assigned_caregiver.id)
                }
            }
        })
        duration = util.date.durationToHours(duration)

        if (!duration) {
            return '';
        }

        const hourText = `${duration} ${duration > 1 ? 'hours' : 'hour'}`
        const caregiverText = `${caregivers.size} ${caregivers.size > 1 ? 'caregivers' : 'caregiver'}`

        return `${message} <span>${hourText} worked by ${caregiverText}</span>`;
    }

    function getNoDataContextHint() {
        return `<span class="font-semibold text-lifeworx-blue-900 ">Tip: </span> <span class=" text-lifeworx-blue-700">Add shifts with rate information for revenue estimates</span>`;
    }

    function getScheduledHint() {
        let totalWeeklyDuration = 0
        let totalWeeklyUnassignedDuration = 0

        const nextSunday = util.date.nextSunday(props.start_date)
        let tipHtml = ''

        if (props.client.type == 'prospect') {
            let totalMinWeeklyRevenue = 0
            let totalMaxWeeklyRevenue = 0
            clientShifts.value.forEach(shift => {
                const startDate = util.getLargerItem(props.start_date, shift.start_date)
                const weeklyDuration = util.date.durationToHours(
                    util.schedule.getShiftDurationWithinRage(shift.day_of_week, startDate, nextSunday, shift.duration)
                )
                totalWeeklyDuration += weeklyDuration

                if (weeklyDuration == 0) {
                    return;
                }

                if (shift.charge_duration_rate_type == 'daily') {
                    totalMinWeeklyRevenue += shift.min_charge_rate_amount
                    totalMaxWeeklyRevenue += shift.max_charge_rate_amount
                } else {
                    totalMinWeeklyRevenue += weeklyDuration * shift.min_charge_rate_amount
                    totalMaxWeeklyRevenue += weeklyDuration * shift.max_charge_rate_amount
                }

                if (!shift.assigned_caregiver.id) {
                    totalWeeklyUnassignedDuration += weeklyDuration
                }
            })
            const totalMinCycleRevenue = 4 * totalMinWeeklyRevenue
            const totalMaxCycleRevenue = 4 * totalMaxWeeklyRevenue

            const rangeWeeklyRevenueText = getRangeText(totalMinWeeklyRevenue, totalMaxWeeklyRevenue)
            const rangeCycleRevenueText = getRangeText(totalMinCycleRevenue, totalMaxCycleRevenue)

            tipHtml +=  `for an estimated <span class="font-semibold text-lifeworx-green-500">${rangeWeeklyRevenueText}</span> revenue per week
                or <span class="font-semibold text-lifeworx-green-500">${rangeCycleRevenueText}</span> per 4 week cycle`
        } else {
            let totalWeeklyRevenue = 0
            clientShifts.value.forEach(shift => {
                const startDate = util.getLargerItem(props.start_date, shift.start_date)
                const weeklyDuration = util.date.durationToHours(
                    util.schedule.getShiftDurationWithinRage(shift.day_of_week, startDate, nextSunday, shift.duration)
                )
                totalWeeklyDuration += weeklyDuration

                if (weeklyDuration == 0) {
                    return;
                }

                if (shift.charge_duration_rate_type == 'daily') {
                    totalWeeklyRevenue += shift.charge_rate_amount
                } else {
                    totalWeeklyRevenue += weeklyDuration * shift.charge_rate_amount
                }

                if (!shift.assigned_caregiver.id) {
                    totalWeeklyUnassignedDuration += weeklyDuration
                }
            })
            const totalCycleRevenue = 4 * totalWeeklyRevenue

            tipHtml += ` for an estimated <span class="font-semibold text-lifeworx-green-500">${util.currencyWithoutFraction(totalWeeklyRevenue)}</span> revenue per week
                or <span class="font-semibold text-lifeworx-green-500">${util.currencyWithoutFraction(totalCycleRevenue)}</span> per 4 week cycle`
        }

        return `<span class="font-semibold text-lifeworx-blue-900 ">Scheduled: </span><span class="font-semibold text-lifeworx-green-500">${totalWeeklyDuration} ${totalWeeklyDuration > 1 ? 'hours' : 'hour'}</span><span> per week </span> ${tipHtml}`;
    }

    function getUnassignedCareContextHint() {
        if (unassignedCareContextHours.value.week == 0 && unassignedCareContextHours.value.month == 0) {
            return '';
        }

        const durationWeek = unassignedCareContextHours.value.week
        const hourWeekText = `${durationWeek} ${durationWeek > 1 || durationWeek == 0 ? 'hours' : 'hour'}`

        const durationMonth = unassignedCareContextHours.value.month
        const hourMonthText = `${durationMonth} ${durationMonth > 1 || durationMonth == 0 ? 'hours' : 'hour'}`

        return `<span class="font-semibold text-lifeworx-blue-900 ">Unfilled Need: </span><span class="font-semibold text-lifeworx-red-500">${hourWeekText}</span><span> of unassigned care next week, </span><span class="font-semibold text-lifeworx-red-500">${hourMonthText}</span><span> in the next month</span>`;
    }

    function getEditShiftContextHint() {
        if (props.selected_shift_ids.length == 0) {
            return '';
        }

        const shiftTypeText = `${props.selected_shift_ids.length} ${props.selected_shift_ids.length > 1 ? 'shifts' : 'shift'}`
        const title = props.selected_shift_ids.length > 1 ? 'Edit Shifts' : 'Edit Shift'
        return `<span class="font-semibold text-lifeworx-blue-900">${title}: </span><span>selected <span class="text-lifeworx-green-500 font-semibold">${shiftTypeText}</span></span>`;
    }

    function getRangeText(min, max) {
        let final = ''
        if (min) {
            final += util.currencyWithoutFraction(min)
        }

        if (max) {
            if (final !== '') {
                final += ` - ${util.currencyWithoutFraction(max)}`
            }
        }
        return final;
    }

    function getAddShiftContextHint() {

        if (props.planned_shifts.length == 0) {
            return '';
        }

        const firstShift = props.planned_shifts[0]
        let contextHintText = ''

        let totalWeeklyDuration = 0
        let totalWeeklyOccurrence = props.planned_shifts.length

        props.planned_shifts.forEach(shift => {
            totalWeeklyDuration += util.date.durationToHours(shift.duration)
        })

        if (props.client.type == 'prospect') {
            let minWeeklyRevenueRange = 0, minCycleRevenueRange = 0
            let maxWeeklyRevenueRange = 0, maxCycleRevenueRange = 0

            if (firstShift.charge_duration_rate_type == 'daily') {
                minWeeklyRevenueRange = totalWeeklyOccurrence * firstShift.min_charge_rate_amount
                maxWeeklyRevenueRange = totalWeeklyOccurrence * firstShift.max_charge_rate_amount
            } else {
                minWeeklyRevenueRange = totalWeeklyDuration * firstShift.min_charge_rate_amount
                maxWeeklyRevenueRange += totalWeeklyDuration * firstShift.max_charge_rate_amount
            }

            minCycleRevenueRange = 4 * minWeeklyRevenueRange
            maxCycleRevenueRange = 4 * maxWeeklyRevenueRange

            const rangeWeeklyRevenueText = getRangeText(minWeeklyRevenueRange, maxWeeklyRevenueRange)
            const rangeCycleRevenueText = getRangeText(minCycleRevenueRange, maxCycleRevenueRange)

            contextHintText = `<span class="font-semibold text-lifeworx-green-500">${totalWeeklyDuration} ${totalWeeklyDuration > 1 ? 'hours' : 'hour'}</span> per week`
            if (rangeWeeklyRevenueText !== '') {
                contextHintText += ` for an estimated <span class="font-semibold text-lifeworx-green-500">${ rangeWeeklyRevenueText }</span> new revenue per week`
            }
            if (rangeCycleRevenueText !== '') {
                contextHintText += ` or <span class="font-semibold text-lifeworx-green-500">${ rangeCycleRevenueText }</span> per 4 week cycle`
            }
        } else {
            let weeklyRevenue, cycleRevenue = 0
            if (firstShift.charge_duration_rate_type == 'daily') {
                weeklyRevenue = totalWeeklyOccurrence * firstShift.charge_rate_amount
            } else {
                weeklyRevenue = totalWeeklyDuration * firstShift.charge_rate_amount
            }
            cycleRevenue = 4 * weeklyRevenue

            contextHintText = `<span class="font-semibold text-lifeworx-green-500">${totalWeeklyDuration} ${totalWeeklyDuration > 1 ? 'hours' : 'hour'}</span> per week`
            if (weeklyRevenue) {
                contextHintText += ` for an estimated <span class="font-semibold text-lifeworx-green-500">${util.currencyWithoutFraction(weeklyRevenue)}</span> new revenue per week`
            }
            if (cycleRevenue) {
                contextHintText += ` or <span class="font-semibold text-lifeworx-green-500">${util.currencyWithoutFraction(cycleRevenue)}</span> per 4 week cycle`
            }
        }

        return `<span class="font-semibold text-lifeworx-blue-900">Adding ${totalWeeklyOccurrence > 1 ? 'shifts' : 'shift'}: </span>${contextHintText}`;
    }

    function handleSaveShiftViewToClipboard() {
        emit('shifts:copy')
        notificationStore.add('Shift Summary Copied to Clipboard')
    }

    const unassignedCareContextHours = computed(() => {
        let durationWeek = 0
        let durationMonth = 0
        const endOfNextMonthPeriodDate = util.date.nextDate(props.start_date, 7 * 5 - 1)
        const nextSunday = util.date.nextSunday(props.start_date)
        const saturdayNext2Weeks = util.date.nextDate(nextSunday, 6)
        const sundayNext2Weeks = util.date.nextDate(nextSunday, 7)
        clientShifts.value.forEach(shift => {
            if (!shift.assigned_caregiver.id) {

                if (shift.end_date == null) {
                    durationWeek +=  util.schedule.getShiftDurationWithinRage(shift.day_of_week, nextSunday, sundayNext2Weeks, shift.duration)
                    durationMonth += util.schedule.getShiftDurationWithinRage(shift.day_of_week, nextSunday, endOfNextMonthPeriodDate, shift.duration)
                } else {
                    const dayAfterShiftEndDate = util.date.nextDate(shift.end_date, 1)
                    if (shift.start_date < sundayNext2Weeks && shift.end_date >= nextSunday) {
                        durationWeek +=  util.schedule.getShiftDurationWithinRage(shift.day_of_week, nextSunday, util.getSmallerItem(dayAfterShiftEndDate, sundayNext2Weeks), shift.duration)
                    }
                    if (shift.start_date < endOfNextMonthPeriodDate && shift.end_date >= nextSunday) {
                        durationMonth +=  util.schedule.getShiftDurationWithinRage(shift.day_of_week, nextSunday, util.getSmallerItem(dayAfterShiftEndDate, endOfNextMonthPeriodDate), shift.duration)
                    }
                }
            }
        })

        durationWeek = util.date.durationToHours(durationWeek)
        durationMonth = util.date.durationToHours(durationMonth)

        return {
            week: durationWeek,
            month: durationMonth
        };
    }, {immediate: true})

</script>

<template>

    <div class="flex border-t items-center px-6 h-14">
        <div
            :class="[
                'flex-auto pt-0.5 text-sm',
                {
                    'text-[13px]': contextHint.indexOf('<br>') !== -1
                }
            ]"
            v-html="contextHint"
        />
        <div class="flex-none p-1 grid justify-center">
            <VTooltip
                :disabled="!canCopy"
                :triggers="['hover']"
                :autoHide="true"
                @apply-hide="handleBlur"
            >
                <button
                    @click.stop="handleSaveShiftViewToClipboard"
                    :disabled="!canCopy"
                    :class="[
                        '-mt-1 p-1 grid justify-center',
                        {
                            'text-stone-300 disabled:text-stone-300 cursor-default': !canCopy,
                            'text-lifeworx-blue-600 hover:text-lifeworx-blue-500 cursor-pointer': canCopy,
                        }
                    ]"
                >
                    <ClipboardDocumentListIcon class="w-6 h-6" />
                </button>
                <template #popper>
                    <span class="text-sm">
                        Copy Shift Summary
                    </span>
                </template>
            </VTooltip>
        </div>

    </div>

</template>
